import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Search, Settings2, X, AlertTriangle, RefreshCcw, Filter, Trash2, RotateCcw, CheckSquare, Square,
} from "lucide-react";
import { api } from "../../lib/api";
import { purchaseStatusLabel } from "../../lib/purchaseStatus";
import PurchaseDrawer from "./PurchaseDrawer";

export const PURCHASE_COLUMNS = [
  { key: "created_at", label: "Data", default: true },
  { key: "id_short", label: "Comandă #", default: true },
  { key: "invoice_id", label: "Order ID", default: false },
  { key: "lead_full_name", label: "Nume complet", default: true },
  { key: "email", label: "Email", default: true },
  { key: "lead_phone", label: "Telefon", default: false },
  { key: "amount", label: "Sumă", default: true },
  { key: "status", label: "Status", default: true },
  { key: "provider", label: "Provider", default: false },
  { key: "netopia_ntp_id", label: "NTP ID", default: false },
  { key: "netopia_status_code", label: "Cod status", default: false },
  { key: "lead_group_name", label: "Grupa", default: false },
  { key: "lead_source", label: "Sursă lead", default: false },
  { key: "lead_status", label: "Status lead", default: false },
  { key: "utm_source", label: "UTM source", default: false },
  { key: "utm_campaign", label: "UTM campaign", default: false },
  { key: "updated_at", label: "Ultim update", default: false },
];

const STATUSES = ["pending", "paid", "abandoned", "failed", "cancelled", "refunded", "duplicate"];
const QUICK_FILTERS = [
  { id: "paid", label: "Plătite", patch: { status: "paid" } },
  { id: "abandoned", label: "Abandonate", patch: { status: "abandoned" } },
  { id: "failed", label: "Eșuate", patch: { status: "failed" } },
  { id: "today", label: "Azi", patch: { range: "today" } },
  { id: "week", label: "Săptămâna asta", patch: { range: "week" } },
];

const STATUS_BADGE = {
  paid:                 "bg-[#519872]/15 text-[#519872]",
  pending:              "bg-[#FDE68A] text-[#8a6a1a]",
  pending_registration: "bg-[#965EA7]/15 text-[#7A4986]",
  abandoned:            "bg-[#6B7280]/15 text-[#6B7280]",
  cancelled:            "bg-[#FDBA74]/30 text-[#9A3412]",
  failed:               "bg-[#E63946]/15 text-[#E63946]",
  duplicate:            "bg-[#965EA7]/25 text-[#7A4986]",
};

const LS_COLUMNS_KEY = "fped_admin_purchase_columns_v1";

export default function PurchasesTab({ canMutate, isOwner, currentEmail }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: "", mode: "", range: "", search: "",
    email: "", payment_provider: "",
    include_deleted: false, include_stale: false, test_only: false,
  });
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showColumns, setShowColumns] = useState(false);
  const [activeColumns, setActiveColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_COLUMNS_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* fall through */ }
    return PURCHASE_COLUMNS.filter((c) => c.default).map((c) => c.key);
  });

  useEffect(() => {
    try { localStorage.setItem(LS_COLUMNS_KEY, JSON.stringify(activeColumns)); } catch { /* noop */ }
  }, [activeColumns]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.mode) params.mode = filters.mode;
      if (filters.range) params.range = filters.range;
      if (filters.search) params.search = filters.search;
      if (filters.email) params.email = filters.email;
      if (filters.payment_provider) params.payment_provider = filters.payment_provider;
      if (filters.include_deleted) params.include_deleted = true;
      if (filters.include_stale) params.include_stale = true;
      if (filters.test_only) params.test_only = true;
      const r = await api.get("/admin/purchases", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setSelected(new Set());
    } catch (e) {
      toast.error("Nu am putut încărca comenzile.");
    } finally {
      setLoading(false);
    }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const applyQuickFilter = (qf) => setFilters((f) => ({ ...f, ...qf.patch }));
  const clearFilters = () => setFilters({
    status: "", mode: "", range: "", search: "",
    email: "", payment_provider: "",
    include_deleted: false, include_stale: false, test_only: false,
  });

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((p) => p.id)));
  };

  const requestDelete = (ids) => {
    if (!isOwner || ids.length === 0) return;
    setDeleteConfirm({ ids, reason: "", confirm_paid_live: false, retry409: false });
  };

  const doDelete = async () => {
    if (!deleteConfirm) return;
    const { ids, reason, confirm_paid_live } = deleteConfirm;
    try {
      if (ids.length === 1) {
        await api.delete(`/admin/purchases/${ids[0]}`, { data: { reason, confirm_paid_live } });
      } else {
        await api.post("/admin/purchases/bulk-delete", { ids, reason, confirm_paid_live });
      }
      toast.success(`${ids.length} comand${ids.length === 1 ? "ă" : "e"} ștearsă${ids.length === 1 ? "" : "e"}`);
      setDeleteConfirm(null);
      setSelected(new Set());
      load();
    } catch (err) {
      if (err?.response?.status === 409) {
        setDeleteConfirm((d) => ({ ...d, retry409: true }));
        toast.message(err?.response?.data?.detail || "Confirmare necesară pentru paid live");
        return;
      }
      toast.error(err?.response?.data?.detail || "Eroare la ștergere");
    }
  };

  const restorePurchase = async (p) => {
    try {
      await api.post(`/admin/purchases/${p.id}/restore`);
      toast.success("Restaurată");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare");
    }
  };

  const renderCell = (p, key) => {
    if (key === "created_at" || key === "updated_at") {
      return p[key] ? new Date(p[key]).toLocaleString("ro-RO") : "—";
    }
    if (key === "id_short") return <span className="font-mono text-xs">#{p.id?.slice(0, 8)}</span>;
    // M901 — `invoice_id` is the human-readable order/invoice number across
    // both providers. Netopia stores it as `netopia_order_id`; EuPlătesc as
    // `euplatesc_invoice_id`. The first truthy wins.
    if (key === "invoice_id") return (
      <span className="font-mono text-xs">{p.netopia_order_id || p.euplatesc_invoice_id || "—"}</span>
    );
    if (key === "amount") {
      if (!p.amount) return "—";
      const sum = `${Number(p.amount).toLocaleString("ro-RO")} ${p.currency || ""}`;
      // Not money that came in online: a place the school is invoiced for, by hand (12.10.2026).
      return p.provider === "institution_invoice"
        ? <span>{sum} <span className="ml-1 rounded-full bg-[#519872]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#3c7457]">factură instituție</span></span>
        : sum;
    }
    if (key === "status") {
      const isStale = p.is_current === false && p.status !== "paid";
      return (
        <span className="inline-flex items-center gap-1">
          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_BADGE[p.status] || ""}`}>
            {purchaseStatusLabel(p.status)}
          </span>
          {isStale && (
            <span
              className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold bg-[#FDE68A]/40 text-[#7A5A1B] border border-[#F5C76E]/40"
              title={p.marked_stale_reason === "enrollment_paid_via_another_link"
                ? "Înscrierea a fost achitată printr-un alt link."
                : "Linkul a fost regenerat — această comandă a fost înlocuită."}
            >
              înlocuit
            </span>
          )}
        </span>
      );
    }
    if (key === "provider") return p.provider === "institution_invoice" ? "factură instituție" : p.provider || "—";
    // M901 — txn-id column is provider-aware: Netopia → ntp_id, EuPlătesc → ep_id.
    if (key === "netopia_ntp_id") return p.netopia_ntp_id || p.euplatesc_ep_id || "—";
    if (key === "netopia_status_code") return (
      p.netopia_status_code ?? p.euplatesc_action ?? "—"
    );
    return p[key] || "—";
  };

  const visibleCols = useMemo(
    () => PURCHASE_COLUMNS.filter((c) => activeColumns.includes(c.key)),
    [activeColumns]
  );

  return (
    <div className="space-y-3" data-testid="purchases-tab">
      {/* Toolbar */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <input
              type="search"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Caută după email, comandă, order ID, NTP ID…"
              className="w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-full pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#965EA7]"
              data-testid="purchases-search"
            />
          </div>
          <button onClick={load} className="rounded-full px-3 py-1.5 text-sm border border-[#E5E7EB] hover:bg-[#EDEEF0] inline-flex items-center gap-1" data-testid="purchases-reload">
            <RefreshCcw size={14} /> Reîncarcă
          </button>
          <button onClick={() => setShowColumns((v) => !v)} className="rounded-full px-3 py-1.5 text-sm border border-[#E5E7EB] hover:bg-[#EDEEF0] inline-flex items-center gap-1" data-testid="purchases-columns-button">
            <Settings2 size={14} /> Coloane
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={14} className="text-[#6B7280]" />
          {QUICK_FILTERS.map((qf) => {
            const active = Object.entries(qf.patch).every(([k, v]) => filters[k] === v);
            return (
              <button
                key={qf.id}
                onClick={() => applyQuickFilter(qf)}
                className={`text-xs rounded-full px-3 py-1 font-semibold border transition-colors ${active ? "bg-[#373A45] text-white border-[#373A45]" : "bg-white text-[#373A45] border-[#E5E7EB] hover:bg-[#EDEEF0]"}`}
                data-testid={`purchases-filter-${qf.id}`}
              >
                {qf.label}
              </button>
            );
          })}
          {(filters.status || filters.mode || filters.range || filters.email || filters.payment_provider || filters.include_deleted || filters.test_only) && (
            <button onClick={clearFilters} className="text-xs rounded-full px-3 py-1 font-semibold bg-[#FDECEC] text-[#9b1f2c] inline-flex items-center gap-1">
              <X size={12} /> Resetează filtre
            </button>
          )}
        </div>

        {/* Owner-only fine filters */}
        {isOwner && (
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <input
              value={filters.email}
              onChange={(e) => setFilters({ ...filters, email: e.target.value })}
              placeholder="Email exact"
              className="rounded-full px-3 py-1.5 text-xs border border-[#E5E7EB] bg-white w-44"
              data-testid="purchases-filter-email"
            />
            <input
              value={filters.payment_provider}
              onChange={(e) => setFilters({ ...filters, payment_provider: e.target.value })}
              placeholder="Provider (mock/netopia…)"
              className="rounded-full px-3 py-1.5 text-xs border border-[#E5E7EB] bg-white w-52"
              data-testid="purchases-filter-provider"
            />
            <label className="inline-flex items-center gap-1.5 text-[#373A45] cursor-pointer">
              <input
                type="checkbox"
                checked={filters.test_only}
                onChange={(e) => setFilters({ ...filters, test_only: e.target.checked })}
                className="accent-[#9b1f2c]"
                data-testid="purchases-filter-testonly"
              />
              Doar test/sandbox
            </label>
            <label className="inline-flex items-center gap-1.5 text-[#373A45] cursor-pointer">
              <input
                type="checkbox"
                checked={filters.include_deleted}
                onChange={(e) => setFilters({ ...filters, include_deleted: e.target.checked })}
                className="accent-[#965EA7]"
                data-testid="purchases-filter-deleted"
              />
              Arată șterse
            </label>
            <label
              className="inline-flex items-center gap-1.5 text-[#373A45] cursor-pointer"
              title="Afișează și încercările vechi (linkuri regenerate sau înlocuite după paid)."
            >
              <input
                type="checkbox"
                checked={filters.include_stale}
                onChange={(e) => setFilters({ ...filters, include_stale: e.target.checked })}
                className="accent-[#7A5A1B]"
                data-testid="purchases-filter-stale"
              />
              Include încercări vechi
            </label>
          </div>
        )}

        {showColumns && (
          <div className="rounded-xl border border-[#E5E7EB] p-3 bg-[#F5F6F6]">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-2">Selectează coloanele</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {PURCHASE_COLUMNS.map((c) => (
                <label key={c.key} className="flex items-center gap-2 text-sm text-[#373A45]">
                  <input
                    type="checkbox"
                    className="accent-[#965EA7]"
                    checked={activeColumns.includes(c.key)}
                    onChange={(e) => setActiveColumns(
                      e.target.checked
                        ? [...activeColumns, c.key]
                        : activeColumns.filter((k) => k !== c.key),
                    )}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-x-auto">
        <table className="w-full text-sm" data-testid="purchases-table">
          <thead className="bg-[#EDEEF0] text-[#373A45]">
            <tr className="text-left">
              {isOwner && (
                <th className="px-3 py-3 w-8">
                  <button
                    onClick={toggleAll}
                    className="text-[#6B7280] hover:text-[#373A45]"
                    aria-label="Selectează tot"
                    data-testid="purchases-select-all"
                  >
                    {items.length > 0 && selected.size === items.length
                      ? <CheckSquare size={14} className="text-[#965EA7]" />
                      : <Square size={14} />}
                  </button>
                </th>
              )}
              {visibleCols.map((c) => (
                <th key={c.key} className="px-4 py-3 text-xs uppercase tracking-widest font-semibold whitespace-nowrap">
                  {c.label}
                </th>
              ))}
              <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Dup.</th>
              {isOwner && <th className="px-4 py-3" />}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={visibleCols.length + (isOwner ? 3 : 1)} className="p-6 text-center text-[#6B7280]">Se încarcă…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={visibleCols.length + (isOwner ? 3 : 1)} className="p-6 text-center text-[#6B7280]">Nicio comandă pentru filtrele curente.</td></tr>
            )}
            {!loading && items.map((p) => (
              <tr
                key={p.id}
                className={`border-t border-[#E5E7EB] hover:bg-[#F5F6F6] cursor-pointer ${p.possible_duplicate ? "bg-[#FBF4FE]" : ""} ${selected.has(p.id) ? "bg-[#F5F0F8]" : ""}`}
                onClick={() => setSelectedId(p.id)}
                data-testid={`purchase-row-${p.id}`}
              >
                {isOwner && (
                  <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => toggleRow(p.id)}
                      data-testid={`purchases-checkbox-${p.id}`}
                      className="text-[#6B7280] hover:text-[#373A45]"
                      aria-label={selected.has(p.id) ? "Deselectează" : "Selectează"}
                    >
                      {selected.has(p.id) ? <CheckSquare size={14} className="text-[#965EA7]" /> : <Square size={14} />}
                    </button>
                  </td>
                )}
                {visibleCols.map((c) => (
                  <td key={c.key} className="px-4 py-3 text-[#373A45] align-top whitespace-nowrap">
                    {renderCell(p, c.key)}
                  </td>
                ))}
                <td className="px-4 py-3 align-top">
                  {p.possible_duplicate && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#965EA7]/15 text-[#7A4986] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest" title="Posibil duplicat — același email + sumă în <5 min">
                      <AlertTriangle size={10} /> Dup
                    </span>
                  )}
                  {p.deleted_at && (
                    <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#6B7280]/15 text-[#373A45] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest" title={`Șters la ${new Date(p.deleted_at).toLocaleString("ro-RO")} de ${p.deleted_by || "?"}`}>
                      Șters
                    </span>
                  )}
                </td>
                {isOwner && (
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {p.deleted_at ? (
                      <button
                        onClick={() => restorePurchase(p)}
                        className="inline-flex items-center gap-1 text-[#519872] hover:underline text-xs"
                        data-testid={`purchases-restore-${p.id}`}
                        title="Restaurează"
                      >
                        <RotateCcw size={12} />
                      </button>
                    ) : (
                      <button
                        onClick={() => requestDelete([p.id])}
                        className="inline-flex items-center gap-1 text-[#9b1f2c] hover:underline text-xs"
                        data-testid={`purchases-delete-${p.id}`}
                        title="Șterge comandă"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-[#6B7280] px-1">
        Total: {total} comenzi · {items.length} afișate
        {selected.size > 0 && <span className="ml-2 text-[#965EA7] font-semibold">· {selected.size} selectate</span>}
      </div>

      {/* Bulk delete bar */}
      {isOwner && selected.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#373A45] text-white rounded-full px-3 py-2 shadow-2xl flex items-center gap-2"
          data-testid="purchases-bulk-bar"
        >
          <span className="text-xs font-semibold pl-3">{selected.size} selectate</span>
          <button
            onClick={() => requestDelete(Array.from(selected))}
            className="rounded-full bg-[#9b1f2c] hover:bg-[#7e1923] px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1"
            data-testid="purchases-bulk-delete"
          >
            <Trash2 size={12} /> Șterge
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="rounded-full px-3 py-1.5 text-xs text-white/80 hover:text-white"
            data-testid="purchases-bulk-clear"
            aria-label="Anulează selecția"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {deleteConfirm && (
        <PurchaseDeleteModal
          state={deleteConfirm}
          onChange={setDeleteConfirm}
          onCancel={() => setDeleteConfirm(null)}
          onConfirm={doDelete}
        />
      )}

      {selectedId && (
        <PurchaseDrawer
          purchaseId={selectedId}
          canMutate={canMutate}
          currentEmail={currentEmail}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}


function PurchaseDeleteModal({ state, onChange, onCancel, onConfirm }) {
  const isBulk = state.ids.length > 1;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onCancel}
      data-testid="purchases-delete-confirm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl border border-[#E5E7EB] shadow-2xl w-full max-w-md"
      >
        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
          <h3 className="font-display text-lg font-bold text-[#9b1f2c] inline-flex items-center gap-2">
            <Trash2 size={18} /> Ștergere {isBulk ? `${state.ids.length} comenzi` : "comandă"}
          </h3>
          <button onClick={onCancel} className="text-[#6B7280] hover:text-[#373A45]" aria-label="Închide">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3 text-sm text-[#373A45]">
          <p>
            Soft-delete: comanda rămâne în DB cu <code className="bg-[#EDEEF0] px-1 rounded text-xs">deleted_at</code> setat,
            și se scrie entry în Audit.
          </p>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#373A45]">Motiv (opțional)</span>
            <input
              type="text"
              value={state.reason}
              onChange={(e) => onChange({ ...state, reason: e.target.value })}
              placeholder="ex. test cleanup / sandbox"
              className="mt-1 w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#965EA7]"
              data-testid="purchases-delete-reason"
            />
          </label>
          {state.retry409 && (
            <div className="rounded-xl bg-[#FDF3E7] border border-[#F59E0B]/40 p-3 text-xs text-[#8a6a1a]">
              <AlertTriangle className="inline" size={12} /> Cel puțin o comandă este <strong>paid LIVE</strong> (provider real).
              Bifează caseta de mai jos ca să continui.
            </div>
          )}
          <label className="flex items-center gap-2 text-xs text-[#373A45]">
            <input
              type="checkbox"
              checked={state.confirm_paid_live}
              onChange={(e) => onChange({ ...state, confirm_paid_live: e.target.checked })}
              className="accent-[#9b1f2c]"
              data-testid="purchases-delete-confirm-paid"
            />
            Confirm că pot șterge inclusiv comenzi paid live (provider real)
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-[#E5E7EB] bg-[#F5F6F6]/60">
          <button onClick={onCancel} className="rounded-full px-4 py-2 text-sm border border-[#E5E7EB] hover:bg-white">
            Anulează
          </button>
          <button
            onClick={onConfirm}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#9b1f2c] hover:bg-[#7e1923] text-white px-5 py-2 text-sm font-semibold"
            data-testid="purchases-delete-go"
          >
            <Trash2 size={14} /> Șterge {isBulk ? state.ids.length : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
